/**
 * Value insights.
 *
 * Opens from the "total value" figure above the table, and answers where
 * that number is coming from. It sends the page's current filters, so the
 * totals here always match the count behind it — the legacy version
 * hand-rolled a subset of the filters and showed unfiltered totals whenever
 * one of the omitted ones was set.
 */
import { useMemo, useState } from 'react';
import { Alert, Group, Loader, Modal, Stack } from '@mantine/core';
import type { ComponentProps } from 'react';
import {
  Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import {
  useLeadInsights, type InsightsDimension, type LeadFilters,
} from '../api/queries';
import { formatGBP, formatNumber } from '../theme';
import {
  BAR_CURSOR, CHART_POSITIVE, GRID_PROPS, X_AXIS_PROPS, Y_AXIS_PROPS, chartAnimation, formatCompactGBP,
} from '../charts';
import {
  ChartCard, ChartTooltip, MonoSegmented, ProportionList, SpeedChip, StatGrid, StatTile,
  usePrefersReducedMotion, type ProportionItem,
} from '../ui';
import { formatCount, formatHour, formatShare, isoDayLabel, titleCase } from '../ui/format';
import { useVocab, type Vocab } from '../vocab';

type Row = { label: string; leads: number; revenue: number; avg_revenue: number };

/*
  Two kinds of dimension. Source, campaign and buyer are categories: ranked
  rows with a bar each, every figure readable without hovering. Hour, day and
  trend are a scale: bars in their natural order, so the shape is the point.
*/
function dimensions(v: Vocab): { value: InsightsDimension; label: string; title: string; kind: 'list' | 'chart' }[] {
  return [
    { value: 'source', label: v.p.sourceTag, title: `Value by ${v.p.sourceTagLower}`, kind: 'list' },
    { value: 'campaign', label: v.One('campaign'), title: `Value by ${v.one('campaign')}`, kind: 'list' },
    { value: 'buyer', label: v.One('buyer'), title: `Value by ${v.one('buyer')}`, kind: 'list' },
    { value: 'hour', label: 'Hour', title: 'Value by hour of day', kind: 'chart' },
    { value: 'day', label: 'Day', title: 'Value by day of week', kind: 'chart' },
    { value: 'trend', label: 'Trend', title: 'Value over time', kind: 'chart' },
  ];
}

const WEEK = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const CHART_H = 240;

/** Puts scale dimensions in their natural order and labels them for a UK reader. */
export function chartRows(rows: Row[], dimension: InsightsDimension) {
  if (dimension === 'hour') {
    const byHour = new Map(rows.map((r) => [Number(r.label), r]));
    // Every hour of the day, so a quiet hour shows as a gap, not a missing bar.
    return Array.from({ length: 24 }, (_, h) => {
      const r = byHour.get(h);
      return { label: formatHour(h), leads: r?.leads ?? 0, revenue: r?.revenue ?? 0, avg_revenue: r?.avg_revenue ?? 0 };
    });
  }
  if (dimension === 'day') {
    return [...rows].sort((a, b) => WEEK.indexOf(a.label) - WEEK.indexOf(b.label));
  }
  return [...rows].sort((a, b) => a.label.localeCompare(b.label)).map((r) => ({ ...r, label: isoDayLabel(r.label) }));
}

function listItems(rows: Row[], total: number, tidy: boolean, v: Vocab): ProportionItem[] {
  const sorted = [...rows].sort((a, b) => b.revenue - a.revenue);
  const max = Math.max(0, ...sorted.map((r) => r.revenue));
  return sorted.map((r) => ({
    id: r.label,
    label: r.label === '(none)' || r.label === '' ? 'None recorded' : tidy ? titleCase(r.label) : r.label,
    detail: `${formatNumber(r.leads)} ${v.word(r.leads, 'lead')} · ${formatGBP(r.avg_revenue)} average`,
    value: formatGBP(r.revenue),
    share: formatShare(r.revenue, total),
    fraction: max > 0 ? r.revenue / max : 0,
    tone: 'positive',
  }));
}

/** Value, then the lead count behind it, which the bar alone cannot show. */
function ValueTooltip(props: ComponentProps<typeof ChartTooltip>) {
  const v = useVocab();
  const first = props.payload?.[0];
  const leads = first?.payload?.leads;
  const payload = first && typeof leads === 'number'
    ? [first, { dataKey: 'leads', name: v.Many('lead'), value: leads, color: 'transparent' }]
    : props.payload;
  return (
    <ChartTooltip
      {...props}
      payload={payload}
      names={{ revenue: 'Value', leads: v.Many('lead') }}
      colors={{ revenue: CHART_POSITIVE, leads: 'transparent' }}
      formatters={{ revenue: formatGBP, leads: formatNumber }}
    />
  );
}

export function LeadInsightsModal({
  opened,
  onClose,
  filters,
}: {
  opened: boolean;
  onClose: () => void;
  filters: LeadFilters;
}) {
  const v = useVocab();
  const [dimension, setDimension] = useState<InsightsDimension>('source');
  const { data, isPending, error } = useLeadInsights(filters, dimension, opened);
  const dims = dimensions(v);
  const meta = dims.find((d) => d.value === dimension) ?? dims[0]!;
  const reduced = usePrefersReducedMotion();

  const rows = useMemo(() => (data?.rows ?? []) as Row[], [data]);
  const total = data?.overall.total_revenue ?? 0;
  // Source names are machine keys (facebook_ads); campaign and buyer names are
  // typed by people and shown as written.
  const items = useMemo(() => listItems(rows, total, dimension === 'source', v), [rows, total, dimension, v]);
  const bars = useMemo(() => chartRows(rows, dimension), [rows, dimension]);
  const empty = !data || rows.length === 0;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size={880}
      title="Where the value comes from"
      styles={{ title: { fontSize: 16, fontWeight: 600, letterSpacing: '-0.01em' } }}
    >
      <Stack gap="md">
        {error && <Alert color="red" variant="light">{error.message}</Alert>}

        {data && (
          <StatGrid columns={4}>
            <StatTile index={0} label={v.Many('lead')} value={data.overall.total_leads} format={formatCount} caption="Matching your filters" />
            <StatTile index={1} label="Total value" value={data.overall.total_revenue} format={formatGBP} caption={`Across those ${v.many('lead')}`} />
            <StatTile index={2} label="Average" value={data.overall.avg_revenue} format={formatGBP} caption={`Per ${v.one('lead')}`} />
            <StatTile index={3} label="Highest" value={data.overall.max_revenue} format={formatGBP} caption={`Single ${v.one('lead')}`} />
          </StatGrid>
        )}

        <ChartCard
          title={meta.title}
          subtitle={meta.kind === 'list' ? 'Ranked by value' : 'UK time'}
          right={
            <MonoSegmented
              value={dimension}
              onChange={(v) => setDimension(v as InsightsDimension)}
              data={dims.map((d) => ({ value: d.value, label: d.label }))}
              aria-label="Break down by"
            />
          }
          bodyHeight={CHART_H}
          empty={!isPending && empty}
          emptyText={`No ${v.many('lead')} match the current filters`}
        >
          {isPending ? (
            <Group justify="center" h={CHART_H}><Loader size="sm" /></Group>
          ) : meta.kind === 'list' ? (
            <div style={{ maxHeight: CHART_H + 80, overflowY: 'auto', overflowX: 'hidden' }}>
              <ProportionList items={items} />
            </div>
          ) : (
            <div role="img" aria-label={meta.title}>
              <ResponsiveContainer width="100%" height={CHART_H}>
                <BarChart data={bars} margin={{ top: 6, right: 0, bottom: 0, left: -4 }} barCategoryGap="22%">
                  <CartesianGrid {...GRID_PROPS} />
                  <XAxis dataKey="label" {...X_AXIS_PROPS} interval="equidistantPreserveStart" minTickGap={14} />
                  <YAxis {...Y_AXIS_PROPS} tickFormatter={formatCompactGBP} />
                  <Tooltip
                    cursor={BAR_CURSOR}
                    isAnimationActive={false}
                    content={<ValueTooltip />}
                  />
                  <Bar dataKey="revenue" name="Value" fill={CHART_POSITIVE} radius={[3, 3, 0, 0]} maxBarSize={28} {...chartAnimation(reduced)} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </ChartCard>
        {data?.query_stats && (
          <Group justify="flex-end" mt={-6}>
            <SpeedChip stats={data.query_stats} />
          </Group>
        )}
      </Stack>
    </Modal>
  );
}
